import {
  createElement,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';

/** Hide only while the current URL is the one that failed. A new URL shows. */
export function shouldHideBrokenImage(
  imageUrl: string | null | undefined,
  failedForUrl: string | null,
): boolean {
  return !imageUrl || failedForUrl === imageUrl;
}

export type DisplayImageElementProps = {
  imageUrl?: string | null;
  className?: string;
  style?: CSSProperties;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  dataName?: string;
  dataId?: string;
  /**
   * Theme elements paint via background-image (pixel-identical to today's Stage/Fan).
   * FREEFORM slide images use an <img> with object-fit.
   */
  mode?: 'background' | 'img';
  objectFit?: 'contain' | 'cover';
  alt?: string;
};

/**
 * Display-only image. Theme Stage/Fan keep background-image + size/position.
 * FREEFORM uses object-fit contain/cover inside a clipped box.
 * A failed load renders nothing (no alt, no broken-image icon).
 */
export function DisplayImageElement({
  imageUrl,
  className,
  style,
  onClick,
  dataName,
  dataId,
  mode = 'background',
  objectFit = 'contain',
}: DisplayImageElementProps): ReactElement | null {
  const [failedForUrl, setFailedForUrl] = useState<string | null>(null);

  if (mode === 'img') {
    if (shouldHideBrokenImage(imageUrl, failedForUrl)) {
      return null;
    }
    return createElement('img', {
      'data-name': dataName,
      'data-id': dataId,
      className,
      style: {
        width: '100%',
        height: '100%',
        objectFit,
        ...style,
      },
      src: imageUrl,
      alt: '',
      onError: () => {
        if (imageUrl) {
          setFailedForUrl(imageUrl);
        }
      },
      onClick,
    });
  }

  return createElement('div', {
    'data-name': dataName,
    'data-id': dataId,
    className,
    style,
    onClick,
  });
}
